/**
 * Data Upload Panel — Upload addresses/workers, load demo data.
 * Supports CSV and XLSX/XLS (both parsed client-side).
 * Only known fields are extracted; extra columns are ignored.
 */

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import ExcelJS from 'exceljs';
import * as api from '../services/routeOptimizerService';
import DataTable from './DataTable';
import ChangeLockIndicator from './ChangeLockIndicator';
import { useJobPolling } from '../hooks/useJobPolling';

const KNOWN_ADDRESS_FIELDS = ['customer_id', 'id', 'name', 'customer_name', 'address', 'city', 'state', 'zip', 'lat', 'lon', 'preferred_technician', 'pref_tech', 'required_days', 'req_days', 'minimum_qualification', 'min_qual', 'service_tier', 'tier', 'service_time', 'service_minutes', 'notes', 'current_day', 'current_technician_id', 'current_technician'];
const KNOWN_WORKER_FIELDS = ['tech_id', 'id', 'name', 'qualification', 'qual', 'start_location', 'home_address', 'days_available'];

/* -------------------------------------------------------------------------- */
/*  Column definitions for DataTable                                          */
/* -------------------------------------------------------------------------- */

const ADDRESS_COLUMNS = [
  { key: 'customer_id', label: 'ID', width: '70px' },
  { key: 'name', label: 'Name', editable: true },
  { key: 'address', label: 'Address', editable: true },
  { key: 'city', label: 'City', editable: true },
  { key: 'state', label: 'State', editable: true, width: '60px' },
  { key: 'zip', label: 'Zip', editable: true, width: '70px' },
  { key: 'lat', label: 'Lat', editable: true, width: '70px' },
  { key: 'lon', label: 'Lon', editable: true, width: '70px' },
  { key: 'preferred_technician', label: 'Pref Tech', editable: true },
  { key: 'required_days', label: 'Req Days', editable: true },
  { key: 'minimum_qualification', label: 'Min Qual', editable: true, width: '80px' },
  { key: 'service_tier', label: 'Tier', editable: true, width: '80px' },
  { key: 'service_time', label: 'Svc Time', editable: true, width: '80px' },
  { key: 'notes', label: 'Notes', editable: true },
];

const WORKER_COLUMNS = [
  { key: 'tech_id', label: 'ID', width: '70px' },
  { key: 'name', label: 'Name', editable: true },
  {
    key: 'qualification', label: 'Qualification', editable: true, width: '120px',
    render: (row) => {
      const q = (row.qualification || row.qual || 'ST').toUpperCase();
      return <span className={`ro-qual-badge ro-qual-${q.toLowerCase()}`}>{q}</span>;
    },
  },
  { key: 'start_location', label: 'Start Location', editable: true },
  { key: 'home_address', label: 'Home Address', editable: true },
  { key: 'days_available', label: 'Days Available', editable: true },
];

/* -------------------------------------------------------------------------- */
/*  Main component                                                            */
/* -------------------------------------------------------------------------- */

export default function DataUploadPanel({ weekId, details, onRefresh, onProceed }) {
  const [uploading, setUploading] = useState(false);
  const [loadingDemo, setLoadingDemo] = useState(null); // null or sim number
  const [message, setMessage] = useState('');
  const [dragOver, setDragOver] = useState(null); // 'addresses' | 'workers' | null
  const [previousData, setPreviousData] = useState(null);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const [previousChecked, setPreviousChecked] = useState(false);
  const [addressUploadMode, setAddressUploadMode] = useState('replace'); // 'replace' | 'merge'
  const [pendingRemovals, setPendingRemovals] = useState(null); // { items: [...] } or null
  const [geocoding, setGeocoding] = useState(false);
  const addrInputRef = useRef(null);
  const workerInputRef = useRef(null);

  // Check if previous data exists and if geocoding is already running on mount
  useEffect(() => {
    const checkPrevious = async () => {
      try {
        const data = await api.getPreviousData(weekId);
        if (data.source_week) setPreviousData(data);
      } catch (err) {
        // Silently fail — feature is optional
      } finally {
        setPreviousChecked(true);
      }
    };
    const checkGeocodeStatus = async () => {
      try {
        const res = await api.getGeocodeStatus(weekId);
        if (res.status === 'pending' || res.status === 'in_progress') {
          setGeocoding(true);
          setMessage(`success:${res.progress || 'Geocoding in progress...'}`);
        }
      } catch {
        // Silently fail
      }
    };
    checkPrevious();
    checkGeocodeStatus();
  }, [weekId]);

  const handleLoadPreviousAddresses = async () => {
    setLoadingPrevious(true);
    setMessage('');
    try {
      await api.uploadAddresses(weekId, previousData.addresses);
      setMessage(`success:Loaded ${previousData.address_count} addresses from ${previousData.source_week.name}`);
      onRefresh();
    } catch (err) {
      setMessage(`error:${err.message}`);
    } finally {
      setLoadingPrevious(false);
    }
  };

  const handleLoadPreviousWorkers = async () => {
    setLoadingPrevious(true);
    setMessage('');
    try {
      await api.uploadWorkers(weekId, previousData.workers);
      setMessage(`success:Loaded ${previousData.worker_count} workers from ${previousData.source_week.name}`);
      onRefresh();
    } catch (err) {
      setMessage(`error:${err.message}`);
    } finally {
      setLoadingPrevious(false);
    }
  };

  const handleLoadPreviousBoth = async () => {
    setLoadingPrevious(true);
    setMessage('');
    try {
      await Promise.all([
        previousData.address_count > 0 ? api.uploadAddresses(weekId, previousData.addresses) : Promise.resolve(),
        previousData.worker_count > 0 ? api.uploadWorkers(weekId, previousData.workers) : Promise.resolve(),
      ]);
      setMessage(`success:Loaded ${previousData.address_count} addresses and ${previousData.worker_count} workers from ${previousData.source_week.name}`);
      onRefresh();
    } catch (err) {
      setMessage(`error:${err.message}`);
    } finally {
      setLoadingPrevious(false);
    }
  };

  const isXlsx = (filename) => /\.(xlsx|xls)$/i.test(filename);

  const handleDrop = (type) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(null);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileUpload(type, file);
  };

  const handleDragOver = (type) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(type);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(null);
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const handleFileUpload = async (type, file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setMessage(`error:File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 10 MB.`);
      return;
    }
    setUploading(true);
    setMessage('');
    try {
      let data;
      if (isXlsx(file.name)) {
        data = await parseXlsx(file, type);
      } else {
        const text = await file.text();
        data = filterKnownFields(parseCSV(text), type);
      }

      let result;
      if (type === 'addresses' && addressUploadMode === 'merge' && hasAddresses) {
        result = await api.mergeAddresses(weekId, data);
        let msg = `Merged addresses: ${result.merged} updated, ${result.added} added, ${result.unchanged} unchanged (${result.total} total)`;
        if (result.regeocode_count > 0) {
          msg += `. ${result.regeocode_count} address(es) will be re-geocoded.`;
        }
        setMessage(`success:${msg}`);
        // Show removal dialog if there are missing IDs
        if (result.potentially_removed?.length > 0) {
          setPendingRemovals({ items: result.potentially_removed });
        }
      } else {
        if (type === 'addresses') {
          result = await api.uploadAddresses(weekId, data);
        } else {
          result = await api.uploadWorkers(weekId, data);
        }
        let msg = `Uploaded ${result.count || result.address_count || result.worker_count || ''} ${type}`;
        if (result.depot) {
          msg += result.depot.source === 'geocoded'
            ? ` — Depot set to: ${result.depot.address}`
            : ` — Depot set to: (${result.depot.lat}, ${result.depot.lon})`;
        }
        setMessage(`success:${msg}`);
      }
      onRefresh();
    } catch (err) {
      setMessage(`error:${err.message}`);
    } finally {
      setUploading(false);
      if (addrInputRef.current) addrInputRef.current.value = '';
      if (workerInputRef.current) workerInputRef.current.value = '';
    }
  };

  const handleLoadDemo = async (simNum) => {
    setLoadingDemo(simNum);
    setMessage('');
    try {
      const result = await api.loadDemo(weekId, simNum);
      setMessage(`success:Loaded demo: ${result.address_count} addresses, ${result.worker_count} workers`);
      onRefresh();
    } catch (err) {
      setMessage(`error:${err.message}`);
    } finally {
      setLoadingDemo(null);
    }
  };

  const handleGeocode = async () => {
    setGeocoding(true);
    setMessage('');
    try {
      const result = await api.geocodeAddresses(weekId);
      if (result.status === 'complete' && result.geocoded === 0) {
        setMessage(`success:${result.message || 'All addresses already have coordinates'}`);
        setGeocoding(false);
        return;
      }
      // Job submitted — polling will handle status updates
      setMessage(`success:Geocoding ${result.missing || ''} addresses... (processing in background)`);
    } catch (err) {
      if (err.response?.status === 409) {
        setMessage('info:Geocoding already in progress...');
      } else {
        setMessage(`error:${err.message}`);
        setGeocoding(false);
      }
    }
  };

  // Poll geocode job status
  const geocodePoll = useJobPolling(
    useCallback(() => api.getGeocodeStatus(weekId), [weekId]),
    geocoding, 2000
  );

  useEffect(() => {
    if (!geocoding) return;
    if (geocodePoll.status === 'complete') {
      setMessage(`success:Geocoding complete — ${geocodePoll.progress || 'done'}`);
      setGeocoding(false);
      onRefresh();
    } else if (geocodePoll.status === 'failed') {
      setMessage(`error:Geocoding failed — ${geocodePoll.error || geocodePoll.progress || 'unknown error'}`);
      setGeocoding(false);
    } else if (geocodePoll.progress) {
      setMessage(`success:${geocodePoll.progress}`);
    }
  }, [geocodePoll.status, geocodePoll.progress, geocodePoll.error, geocoding, onRefresh]);

  const hasAddresses = details.address_count > 0;
  const hasWorkers = details.worker_count > 0;

  // Normalize address field names for display
  const normalizedAddresses = (details.addresses || []).map(a => ({
    customer_id: a.customer_id || a.id || '',
    name: a.name || a.customer_name || '',
    address: a.address || a.street || '',
    city: a.city || '',
    state: a.state || '',
    zip: a.zip || a.zipcode || '',
    lat: a.lat || '',
    lon: a.lon || '',
    preferred_technician: a.preferred_technician || a.pref_tech || '',
    required_days: a.required_days || a.req_days || '',
    minimum_qualification: a.minimum_qualification || a.min_qual || '',
    service_tier: a.service_tier || a.tier || '',
    service_time: a.service_time || a.service_minutes || '',
    notes: a.notes || '',
    last_change_date: a.last_change_date || '',
  }));

  const normalizedWorkers = (details.workers || []).map(w => ({
    tech_id: w.tech_id || w.id || '',
    name: w.name || '',
    qualification: w.qualification || w.qual || 'ST',
    start_location: w.start_location || '',
    home_address: w.home_address || '',
    days_available: w.days_available || '',
  }));

  const handleAddressEdit = useCallback((index, key, value) => {
    // Optimistic update in UI; persist on save
    // For now, inline edits call the API directly
    api.updateAddresses(weekId, [{ index, [key]: value }]).then(onRefresh).catch(() => {});
  }, [weekId, onRefresh]);

  const handleWorkerEdit = useCallback((index, key, value) => {
    api.updateWorkers(weekId, [{ index, [key]: value }]).then(onRefresh).catch(() => {});
  }, [weekId, onRefresh]);

  const handleLockOverride = useCallback((index) => {
    api.overrideLock(weekId, index).then(onRefresh).catch(() => {});
  }, [weekId, onRefresh]);

  const handleAddressBulkEdit = useCallback((indices, key, value) => {
    const updates = indices.map(index => ({ index, [key]: value }));
    api.updateAddresses(weekId, updates).then(onRefresh).catch(() => {});
  }, [weekId, onRefresh]);

  const handleWorkerBulkEdit = useCallback((indices, key, value) => {
    const updates = indices.map(index => ({ index, [key]: value }));
    api.updateWorkers(weekId, updates).then(onRefresh).catch(() => {});
  }, [weekId, onRefresh]);

  const lockColumn = useMemo(() => [{
    key: '_lock',
    label: 'Lock',
    width: '50px',
    render: (row, originalIndex) => (
      <ChangeLockIndicator
        lastChangeDate={row.last_change_date}
        onOverride={() => handleLockOverride(originalIndex)}
      />
    ),
  }], [handleLockOverride]);

  // Row highlighting for addresses missing coordinates
  const addressRowClassName = useCallback((row) => {
    if (!row.lat && !row.lon) return 'ro-dt-row-error';
    return '';
  }, []);

  // Column filters for address table
  const addressColumnFilters = useMemo(() => {
    const tiers = [...new Set(normalizedAddresses.map(a => a.service_tier).filter(Boolean))].sort();
    const days = [...new Set(normalizedAddresses.map(a => a.required_days).filter(Boolean))].sort();
    const filters = [];
    if (tiers.length > 1) {
      filters.push({
        key: 'service_tier', label: 'Tier',
        options: tiers.map(t => ({ value: t, label: t })),
      });
    }
    if (days.length > 0) {
      filters.push({
        key: 'required_days', label: 'Fixed Day',
        options: days.map(d => ({ value: d, label: d })),
      });
    }
    filters.push({
      key: '_lock_status', label: 'Lock',
      options: [{ value: 'locked', label: 'Locked' }, { value: 'unlocked', label: 'Unlocked' }],
      filterFn: (row, val) => {
        const hasLock = row.last_change_date && (Date.now() - new Date(row.last_change_date).getTime()) < 90 * 24 * 60 * 60 * 1000;
        return val === 'locked' ? !!hasLock : !hasLock;
      },
    });
    return filters;
  }, [normalizedAddresses]);

  return (
    <div className="ro-panel">
      <h3>Data Upload</h3>

      {/* Load Previous Data card */}
      {previousChecked && previousData && (
        <div className="ro-previous-data-card">
          <h4>Load Previous Data</h4>
          <p>
            Reuse data from <strong>{previousData.source_week.name}</strong>:
            {' '}{previousData.address_count} addresses, {previousData.worker_count} workers
          </p>
          <div className="ro-previous-data-buttons">
            <button
              className="ro-btn ro-btn-secondary"
              onClick={handleLoadPreviousAddresses}
              disabled={loadingPrevious || !previousData.address_count}
            >
              Load Addresses
            </button>
            <button
              className="ro-btn ro-btn-secondary"
              onClick={handleLoadPreviousWorkers}
              disabled={loadingPrevious || !previousData.worker_count}
            >
              Load Workers
            </button>
            <button
              className="ro-btn ro-btn-primary"
              onClick={handleLoadPreviousBoth}
              disabled={loadingPrevious}
            >
              {loadingPrevious ? 'Loading...' : 'Load Both'}
            </button>
          </div>
        </div>
      )}

      {/* Two-column upload zones */}
      <div className="ro-upload-columns">
        {/* ---- Addresses upload zone ---- */}
        <div className="ro-upload-col">
          <div className="ro-upload-group">
            <label>Addresses</label>
            <div
              className={`ro-file-drop${dragOver === 'addresses' ? ' ro-file-drop-active' : ''}`}
              onClick={() => addrInputRef.current?.click()}
              onDrop={handleDrop('addresses')}
              onDragOver={handleDragOver('addresses')}
              onDragEnter={handleDragOver('addresses')}
              onDragLeave={handleDragLeave}
            >
              <input
                ref={addrInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={e => handleFileUpload('addresses', e.target.files[0])}
                disabled={uploading}
                className="ro-file-input-hidden"
              />
              <span className="ro-file-drop-icon">+</span>
              <span className="ro-file-drop-text">
                {uploading ? 'Uploading...' : dragOver === 'addresses' ? 'Drop file here' : 'Choose or drop CSV / Excel file'}
              </span>
              <span className="ro-file-drop-hint">.csv, .xlsx, .xls</span>
            </div>
            {hasAddresses && (
              <span className="ro-tag ro-tag-success">{details.address_count} addresses loaded</span>
            )}
            {hasAddresses && (
              <div className="ro-upload-mode-toggle">
                <label className="ro-upload-mode-option">
                  <input
                    type="radio"
                    name="addressUploadMode"
                    value="replace"
                    checked={addressUploadMode === 'replace'}
                    onChange={() => setAddressUploadMode('replace')}
                  />
                  Replace all
                </label>
                <label className="ro-upload-mode-option">
                  <input
                    type="radio"
                    name="addressUploadMode"
                    value="merge"
                    checked={addressUploadMode === 'merge'}
                    onChange={() => setAddressUploadMode('merge')}
                  />
                  Merge (update existing, add new)
                </label>
              </div>
            )}
          </div>
        </div>

        {/* ---- Workers upload zone ---- */}
        <div className="ro-upload-col">
          <div className="ro-upload-group">
            <label>Workers</label>
            <div
              className={`ro-file-drop${dragOver === 'workers' ? ' ro-file-drop-active' : ''}`}
              onClick={() => workerInputRef.current?.click()}
              onDrop={handleDrop('workers')}
              onDragOver={handleDragOver('workers')}
              onDragEnter={handleDragOver('workers')}
              onDragLeave={handleDragLeave}
            >
              <input
                ref={workerInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={e => handleFileUpload('workers', e.target.files[0])}
                disabled={uploading}
                className="ro-file-input-hidden"
              />
              <span className="ro-file-drop-icon">+</span>
              <span className="ro-file-drop-text">
                {uploading ? 'Uploading...' : dragOver === 'workers' ? 'Drop file here' : 'Choose or drop CSV / Excel file'}
              </span>
              <span className="ro-file-drop-hint">.csv, .xlsx, .xls</span>
            </div>
            {hasWorkers && (
              <span className="ro-tag ro-tag-success">{details.worker_count} workers loaded</span>
            )}
          </div>
        </div>
      </div>

      {/* Data quality summary */}
      {hasAddresses && (() => {
        const withCoords = normalizedAddresses.filter(a => a.lat && a.lon).length;
        const missingCoords = normalizedAddresses.length - withCoords;
        const missingCity = normalizedAddresses.filter(a => !a.city).length;
        const missingZip = normalizedAddresses.filter(a => !a.zip).length;
        const withConstraints = normalizedAddresses.filter(a => a.preferred_technician || a.required_days || a.minimum_qualification).length;
        return (
          <div className="ro-tag-row ro-data-quality-row">
            <span className={`ro-tag ${withCoords === normalizedAddresses.length ? 'ro-tag-success' : 'ro-tag-warning'}`}>
              {withCoords}/{normalizedAddresses.length} geocoded
            </span>
            {missingCoords > 0 && (
              <button
                className="ro-btn ro-btn-sm ro-btn-secondary"
                onClick={handleGeocode}
                disabled={geocoding}
              >
                {geocoding ? 'Geocoding...' : 'Geocode Addresses'}
              </button>
            )}
            {missingCity > 0 && <span className="ro-tag ro-tag-warning">{missingCity} missing city</span>}
            {missingZip > 0 && <span className="ro-tag ro-tag-warning">{missingZip} missing zip</span>}
            {withConstraints > 0 && <span className="ro-tag">{withConstraints} with constraints</span>}
          </div>
        );
      })()}

      {/* Full address data table */}
      {hasAddresses && (
        <>
          <h4 className="ro-subsection-title ro-subsection-title-spaced">Addresses ({details.address_count})</h4>
          <DataTable
            columns={ADDRESS_COLUMNS}
            data={normalizedAddresses}
            pageSize={50}
            onEdit={handleAddressEdit}
            extraColumns={lockColumn}
            selectable
            onBulkEdit={handleAddressBulkEdit}
            columnFilters={addressColumnFilters}
            rowClassName={addressRowClassName}
          />
        </>
      )}

      {/* Full worker data table */}
      {hasWorkers && (
        <>
          <h4 className="ro-subsection-title ro-subsection-title-spaced">Workers ({details.worker_count})</h4>
          <DataTable
            columns={WORKER_COLUMNS}
            data={normalizedWorkers}
            pageSize={25}
            onEdit={handleWorkerEdit}
            selectable
            onBulkEdit={handleWorkerBulkEdit}
          />
        </>
      )}

      {/* Pending removals dialog */}
      {pendingRemovals && (
        <div className="ro-section">
          <div className="ro-validation-warning">
            <strong>{pendingRemovals.items.length} address(es) in existing data were not in the upload</strong>
            <ul style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '12px' }}>
              {pendingRemovals.items.slice(0, 20).map((item, i) => (
                <li key={i}>{item.customer_id} — {item.name || item.address || 'Unknown'}</li>
              ))}
              {pendingRemovals.items.length > 20 && <li>...and {pendingRemovals.items.length - 20} more</li>}
            </ul>
            <div className="ro-button-group" style={{ marginTop: '8px' }}>
              <button
                className="ro-btn ro-btn-danger ro-btn-sm"
                onClick={async () => {
                  try {
                    const ids = pendingRemovals.items.map(r => r.customer_id);
                    await api.confirmRemovals(weekId, ids);
                    setMessage(`success:Removed ${ids.length} address(es)`);
                    setPendingRemovals(null);
                    onRefresh();
                  } catch (err) {
                    setMessage(`error:${err.message}`);
                  }
                }}
              >
                Remove {pendingRemovals.items.length} address(es)
              </button>
              <button
                className="ro-btn ro-btn-sm"
                onClick={() => setPendingRemovals(null)}
              >
                Keep them
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Distance matrix status */}
      <div className="ro-matrix-status">
        {details.has_distance_matrix ? (
          <span className="ro-matrix-ready">Distance matrix ready</span>
        ) : (
          <span className="ro-matrix-none">No distance matrix</span>
        )}
      </div>

      <hr />

      <h3>Load Demo Data</h3>
      <p className="ro-form-description">
        Load pre-built simulation data to test the optimizer without uploading your own files.
      </p>
      <div className="ro-demo-buttons">
        {[
          { sim: 0, label: 'Sim 0 (no constraints)' },
          { sim: 1, label: 'Sim 1 (full constraints)' },
          { sim: 2, label: 'Sim 2 (day constraints)' },
          { sim: 3, label: 'Sim 3 (half constraints)' },
        ].map(({ sim, label }) => (
          <button
            key={sim}
            className="ro-btn ro-btn-sm"
            onClick={() => handleLoadDemo(sim)}
            disabled={loadingDemo != null}
          >
            {loadingDemo === sim ? `Loading ${label}...` : label}
          </button>
        ))}
      </div>

      {message && (() => {
        const isSuccess = message.startsWith('success:');
        const isError = message.startsWith('error:');
        const text = message.replace(/^(success|error):/, '');
        const cls = isSuccess ? 'ro-message-success' : isError ? 'ro-message-error' : 'ro-message';
        return <div className={cls}>{text}</div>;
      })()}

      {/* Proceed to Verify */}
      {onProceed && hasAddresses && hasWorkers && (
        <div className="ro-section-divider">
          <button className="ro-btn ro-btn-primary" onClick={onProceed}>
            Proceed to Verification
          </button>
        </div>
      )}
    </div>
  );
}

function filterKnownFields(rows, type) {
  const known = type === 'addresses' ? KNOWN_ADDRESS_FIELDS : KNOWN_WORKER_FIELDS;
  return rows.map(row => {
    const filtered = {};
    for (const [key, val] of Object.entries(row)) {
      if (known.includes(key.toLowerCase())) {
        filtered[key.toLowerCase()] = val;
      }
    }
    return filtered;
  });
}

async function parseXlsx(file, type) {
  const buffer = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);
  const sheet = wb.worksheets[0];
  if (!sheet || sheet.rowCount < 2) return [];

  // Read headers from row 1, normalize to lowercase
  const headerRow = sheet.getRow(1);
  const headers = [];
  headerRow.eachCell({ includeEmpty: true }, (cell, colNum) => {
    headers[colNum] = (cell.value || '').toString().trim().toLowerCase();
  });

  const known = type === 'addresses' ? KNOWN_ADDRESS_FIELDS : KNOWN_WORKER_FIELDS;
  const rows = [];
  sheet.eachRow({ includeEmpty: false }, (row, rowNum) => {
    if (rowNum === 1) return; // skip header
    const obj = {};
    row.eachCell({ includeEmpty: true }, (cell, colNum) => {
      const header = headers[colNum];
      if (header && known.includes(header)) {
        obj[header] = cell.value != null ? cell.value.toString().trim() : '';
      }
    });
    if (Object.keys(obj).length > 0) rows.push(obj);
  });
  return rows;
}

function parseCSV(text) {
  // Handle both \r\n and \n line endings
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  const parseRow = (line) => {
    // Handle quoted fields (e.g., "123 Main St, Suite 4")
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    values.push(current.trim());
    return values;
  };

  const headers = parseRow(lines[0]).map(h => h.toLowerCase());
  return lines.slice(1).map(line => {
    const values = parseRow(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = values[i] || ''; });
    return obj;
  });
}

